import { crudPage } from '../ui/crud.js';
import { loader } from '../ui/loader.js';
import { badgeStatus, badgeSituacao } from '../ui/badges.js';
import { el, clear, fmtDataHora, formatarNome } from '../lib/dom.js';
import { renderTabela } from '../ui/table.js';
import { listarSugestoesPendentes, aprovarSugestao, recusarSugestao } from '../domain/sugestoes.js';
import { toast } from '../lib/toast.js';
import { imprimirEtiquetasChaves } from '../ui/etiquetas.js';
import { assinarTabela, pollPeriodico } from '../lib/realtime.js';
import { supabase } from '../config/supabaseClient.js';
import { buscarTodos } from '../lib/db.js';
import { abrirAprovacao } from '../ui/aprovarComSimilar.js';
import { recusarComMotivo } from '../ui/recusarComMotivo.js';
import { abrirModal } from '../ui/modal.js';

const OPCOES_STATUS = [{ value: 'ATIVO', label: 'Ativo' }, { value: 'INATIVO', label: 'Inativo' }];

async function alternarStatusChave(l, api) {
  const novo = l.status === 'ATIVO' ? 'INATIVO' : 'ATIVO';
  const { error } = await supabase.from('chave').update({ status: novo }).eq('id', l.id);
  if (error) { toast('Erro: ' + error.message, 'erro'); return; }
  toast(novo === 'INATIVO' ? 'Chave inativada.' : 'Chave reativada.', 'sucesso');
  api.recarregar?.();
}
function excluirChave(l, api) {
  abrirModal({
    titulo: 'Excluir chave',
    corpo: [el('p', { class: 'view__sub', style: 'margin-top:0', text: `Excluir a chave ${l.numero}${l.descricao ? ' — ' + l.descricao : ''}? Ela nunca foi pedida. Esta ação não pode ser desfeita.` })],
    acoes: [
      { rotulo: 'Cancelar', classe: 'btn' },
      { rotulo: 'Excluir', classe: 'btn btn--perigo', onClick: async () => {
        const { error } = await supabase.from('chave').delete().eq('id', l.id);
        if (error) { toast('Não foi possível excluir: ' + error.message, 'erro'); return false; }
        toast('Chave excluída.', 'sucesso'); api.recarregar?.(); return true;
      } },
    ],
  });
}

const crud = crudPage({
  titulo: 'Chaves', singular: 'chave', semCabecalho: true,
  subtitulo: 'Cadastro de chaves. O número é gerado automaticamente pelo sistema.',
  tabela: 'chave', ordenarPor: 'numero', asc: true,
  colunas: [
    { chave: 'numero', rotulo: 'Número' },
    { chave: 'descricao', rotulo: 'Descrição' },
    { chave: 'pedidas', rotulo: 'Pedidas', render: (l) => (l.pedidas == null ? '—' : String(l.pedidas)) },
    { chave: 'situacao', rotulo: 'Situação', render: (l) => badgeSituacao(l.situacao) },
    { chave: 'status', rotulo: 'Status', render: (l) => badgeStatus(l.status) },
  ],
  buscarEm: ['numero', 'descricao'],
  enriquecer: async (linhas) => {
    let mapa = null;
    try {
      const { data, error } = await supabase.rpc('chave_usos');
      if (!error) { mapa = {}; (data || []).forEach((x) => { mapa[x.chave_id] = { pedidas: Number(x.pedidas), total: Number(x.total) }; }); }
    } catch (e) { /* RPC ausente (0052 nao rodada) */ }
    linhas.forEach((l) => { const m = mapa ? mapa[l.id] : null; l.pedidas = mapa ? (m ? m.pedidas : 0) : null; l._total = mapa ? (m ? m.total : 0) : null; });
  },
  acoesLinha: (l, api) => {
    const out = [];
    const ativo = l.status === 'ATIVO';
    out.push(el('button', { class: 'btn', title: ativo ? 'Inativar chave' : 'Reativar chave', 'aria-label': ativo ? 'Inativar' : 'Reativar', onClick: () => alternarStatusChave(l, api) }, [el('i', { class: ativo ? 'ph ph-prohibit' : 'ph ph-arrow-counter-clockwise' })]));
    if (l._total === 0) out.push(el('button', { class: 'btn btn--perigo', title: 'Excluir chave (nunca pedida)', 'aria-label': 'Excluir', onClick: () => excluirChave(l, api) }, [el('i', { class: 'ph ph-trash' })]));
    return out;
  },
  campos: [
    { chave: 'descricao', rotulo: 'Descrição (setor/sala)' },
    { chave: 'status', rotulo: 'Status', tipo: 'select', opcoes: OPCOES_STATUS, padrao: 'ATIVO' },
  ],
});

function tipoBadge(t) {
  return el('span', { class: `badge ${t === 'CRIAR' ? 'badge--ok' : 'badge--alerta'}`, text: t === 'CRIAR' ? 'Criar' : 'Cancelar' });
}
function alvo(l) {
  if (l.tipo === 'CRIAR') return l.descricao || '(nova chave)';
  return l.chave ? `${l.chave.numero}${l.chave.descricao ? ' — ' + l.chave.descricao : ''}` : '—';
}
function traduzErro(msg = '') {
  if (/duplicate|unique/i.test(msg)) return 'Já existe uma chave com esse número.';
  return 'Erro: ' + msg;
}

/** Abre a janela de cadastro de chave (atalho usado na Movimentação). */
export function abrirCadastroChave(aoSalvar) { crud.abrirNovo(undefined, aoSalvar); }

export const paginaChaves = {
  async render(container) {
    container.innerHTML = '';
    container.append(
      el('h1', { class: 'view__titulo', text: 'Chaves' }),
      el('p', { class: 'view__sub', text: 'Cadastro de chaves. O número é gerado pelo sistema; a situação vem da movimentação.' }),
    );

    const badge = el('span', { class: 'aba__contador', text: '…' });
    const btnCad = el('button', { class: 'aba aba--ativa', onClick: () => trocar('cad') }, 'Cadastradas');
    const btnPend = el('button', { class: 'aba', onClick: () => trocar('pend') }, [el('span', { text: 'Pendentes ' }), badge]);
    const divCad = el('div');
    const divPend = el('div', { style: 'display:none' });
    const btnEtq = el('button', { class: 'btn', title: 'Gerar etiquetas de codigo de barras das chaves (folha A4)', onClick: () => imprimirEtiquetasChaves() }, [el('i', { class: 'ph ph-barcode' }), ' Imprimir etiquetas']);
    container.append(el('div', { class: 'chaves-topo' }, [el('div', { class: 'abas' }, [btnCad, btnPend]), btnEtq]), divCad, divPend);

    crud.render(divCad);

    let pendFeito = false;
    function trocar(a) {
      btnCad.classList.toggle('aba--ativa', a === 'cad');
      btnPend.classList.toggle('aba--ativa', a === 'pend');
      divCad.style.display = a === 'cad' ? '' : 'none';
      divPend.style.display = a === 'pend' ? '' : 'none';
      if (a === 'pend' && !pendFeito) { pendFeito = true; desenharPend(); }
    }

    async function desenharPend() {
      clear(divPend);
      const box = el('div');
      divPend.append(el('p', { class: 'view__sub', text: 'Sugestões dos operadores aguardando aprovação.' }), box);
      box.append(loader());
      const linhas = await listarSugestoesPendentes();
      badge.textContent = String(linhas.length);
      clear(box);
      if (!linhas.length) { box.append(el('p', { class: 'view__sub', text: 'Nenhuma sugestão pendente.' })); return; }
      renderTabela(box, {
        colunas: [
          { chave: 'tipo', rotulo: 'Tipo', render: (l) => tipoBadge(l.tipo) },
          { chave: 'alvo', rotulo: 'Chave', render: alvo },
          { chave: 'motivo', rotulo: 'Motivo' },
          { chave: 'operador_nome', rotulo: 'Operador', render: (l) => formatarNome(l.operador_nome || '') },
          { chave: 'sugerido_em', rotulo: 'Quando', render: (l) => fmtDataHora(l.sugerido_em) },
          { chave: '_acoes', rotulo: '', ordenavel: false, render: (l) => el('div', { style: 'display:flex;gap:6px;justify-content:flex-end' }, [
            el('button', { class: 'btn btn--primario', onClick: async () => {
              if (l.tipo === 'CRIAR') {
                const existentes = (await buscarTodos(() => supabase.from('chave').select('id, descricao'))).filter((c) => c.descricao);
                abrirAprovacao({
                  titulo: 'Aprovar chave', valorInicial: l.descricao || '', existentes, getNome: (c) => c.descricao, rotuloConfirmar: 'Aprovar',
                  aoConfirmar: async (nomeFinal) => {
                    const { erro } = await aprovarSugestao(l, nomeFinal);
                    if (erro) { toast(traduzErro(erro), 'erro'); return false; }
                    toast('Sugestão aprovada.', 'sucesso'); crud.recarregar?.(); desenharPend(); return true;
                  },
                });
              } else {
                const { erro } = await aprovarSugestao(l);
                if (erro) { toast(traduzErro(erro), 'erro'); return; }
                toast('Sugestão aprovada.', 'sucesso'); crud.recarregar?.(); desenharPend();
              }
            } }, [el('i', { class: 'ph ph-check' }), ' Aprovar']),
            el('button', { class: 'btn', onClick: () => recusarComMotivo(async (motivo) => {
              const { erro } = await recusarSugestao(l.id, motivo);
              if (erro) { toast('Erro: ' + erro, 'erro'); return false; }
              toast('Sugestão recusada.', 'info'); desenharPend(); return true;
            }) }, [el('i', { class: 'ph ph-x' }), ' Recusar']),
          ]) },
        ],
        linhas, buscarEm: ['numero', 'descricao', 'motivo'], porPagina: 10,
      });
    }

    listarSugestoesPendentes().then((l) => { badge.textContent = String(l.length); });
    const aoMudarPend = () => { if (pendFeito) desenharPend(); else listarSugestoesPendentes().then((l) => { badge.textContent = String(l.length); }); };
    assinarTabela('chave_sugestao', aoMudarPend);
    pollPeriodico(aoMudarPend);
  },
};

